import { OnImageSaveProps } from '@components/form-builder/form-builder';
import { Field } from '@components/form/dynamic-fields';
import { FieldType, FileFieldType, FormV2 } from '@components/form/v2';

import { isInTemplateRoute } from 'ui/router';

import { request } from '../request';

export type ListFormTemplate = {
  shortId: string;
  name: string;
  description: string | null;
  workspace: string;
};

export type FormTemplateBase = { shortId: string; name: string; description: string | null };

export type FormTemplate = FormTemplateBase & {
  fieldsConfig: Array<Field> | FormV2;
  submissionMessageConfig: FormV2;
  status: string; // @TODO: string??
  removedImages: string[];
  workspace: string;
};

export type FormAnswers = Array<{
  h3: string;
  answers: FormAnswer[];
}>;

export type Form = {
  name: string;
  isComplete: boolean;
  uuid: string;
  formAnswers: FormAnswers;
  fieldsConfig: Array<Field>;
};

export type FormAnswerFieldType =
  | Exclude<FieldType, { kind: 'input-file' }>
  | (Omit<FileFieldType, 'value'> & { value?: Array<FormFile> });
export type FormAnswersV2 = Array<FormAnswerFieldType>;

export type FormResponseV2 = {
  name: string;
  description: string;
  isComplete: boolean;
  uuid: string;
  formAnswers: FormAnswersV2;
  fieldsConfig: FormV2;
};

export type FormAnswer =
  | {
      id: string;
      label: string;
      value: string;
      type: 'dateField' | 'formField' | 'dropdown' | 'radioGroup' | 'selectMulti';
    }
  | {
      id: string;
      label: string;
      value: FormFile;
      type: 'file';
    }
  | {
      id: string;
      label: string;
      value: FormFile[];
      type: 'file-multi';
    };

export type FormFile = {
  name: string;
  src: string;
  srcConverted?: string;
};

export type FormTemplateJourney = {
  shortId: string;
  title: string;
  description: string;
  numNodeActions: number;
  numTotalTravellers: number;
  status: 'published' | 'unpublished' | 'old';
  workspace: string;
};

export type FormTemplateImageResponse = {
  imageId: string;
};

export const loadFormTemplates = async (queryString?: string) => {
  const url = isInTemplateRoute() ? `v1/templates/form-templates` : 'v1/form-templates';
  const response = await request.get<ListFormTemplate[]>(`${url}/${queryString ?? ''}`);
  return response.data;
};

export const loadForm = async (shortId: string) => {
  const response = await request.get<Form | FormResponseV2>(`v1/forms/${shortId}/form/`);
  return response.data;
};

export const loadFormTemplate = async (shortId: string) => {
  const url = isInTemplateRoute()
    ? `v1/templates/form-templates/${shortId}/`
    : `v1/form-templates/${shortId}/`;
  const response = await request.get<FormTemplate>(url);
  return response.data;
};

export const saveFormTemplate = async (template: Partial<FormTemplate>) => {
  const response = await request.patch<FormTemplate>(
    `v1/form-templates/${template.shortId}/`,
    template
  );
  return response.data;
};

export const createFormTemplate = async (template: Partial<FormTemplate>) => {
  const response = await request.post<ListFormTemplate>(`v1/form-templates/`, template);
  return response.data;
};

export const deleteFormTemplate = async (shortId: string) => {
  const response = await request.delete(`v1/form-templates/${shortId}/`);
  return response.data;
};

export const duplicateFormTemplate = async (shortId: string, data: { name: string }) => {
  const response = await request.post(`v1/form-templates/${shortId}/duplicate/`, data);
  return response.data;
};

export const moveFormTemplate = async (shortId: string, workspaceId: string) => {
  return await request.post(`v1/form-templates/${shortId}/move/`, { workspace: workspaceId });
};

export const loadFormTemplateJourneys = async (shortId: string) => {
  const response = await request.get<FormTemplateJourney[]>(
    `v1/form-templates/${shortId}/journeys/`
  );
  return response.data;
};

export const createFormTemplateImage = async ({ data, setProgress }: OnImageSaveProps) => {
  const response = await request.post<FormTemplateImageResponse>(`v1/form-templates/image/`, data, {
    onUploadProgress: (progressEvent) => {
      if (setProgress) {
        const { loaded, total } = progressEvent;
        if (total) {
          setProgress(loaded / total);
        }
      }
    },
  });
  return response.data;
};
